import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { setSection } from "../../../assignmentSlice";
import { normalizeEnglishAnswer } from "../../../../../utils/textUtils";

import "./ExamPaperPage.css";

// 문제집 그대로 풀기(2026-10-25 대표: "딱 봐도 문제집 그대로 푸는 게 낫지 않겠니").
// 지인에듀 인쇄용 쪽 그림 위에서 푼다. 좌표(question.exam_pages, api/assignment/exam_paper.py)로
// 지금 문항만 잘라 크게 보여 주고, 쪽 위의 ①~⑤ 자리를 누르면 연필 동그라미를 친다.
// 채점 · 저장은 다른 모의고사 화면과 같다(pageList 의 input · isCorrect · isSubmitted).
// 서술형은 지인에듀가 주는 답 칸 좌표(field.answers)에 바로 쓴다. 좌표가 없거나 칸 수가 우리 빈칸 수와 다르면
// 잘라 낸 문항 아래에 입력 칸을 붙인다. 문항이 쪽을 넘으면 같은 key 의 조각을 쪽마다 이어 붙여 보여 준다.

const CROP_MARGIN = 0.012;
const MIN_TOUCH_SIZE = 34;
// 쪽 그림 원본(폭 1600) 대비 가장 크게 보여 줄 배율. 넓은 화면에서 글자가 지나치게 커지지 않게 한다.
const MAX_CROP_SCALE = 0.72;

// 문항 번호별 조각 목록 [{field, pageIndex}]. 쪽을 넘는 문항은 조각이 둘 이상이다.
const fieldFragmentsOf = (examPageList) => {
  const fragmentsByNumber = {};
  examPageList.forEach((examPage, pageIndex) => {
    (examPage.fields || []).forEach((field) => {
      const number = String(field.key || "").split(":")[1];
      if (!number) return;
      fragmentsByNumber[number] = [...(fragmentsByNumber[number] || []), { field, pageIndex }];
    });
  });
  return fragmentsByNumber;
};

// 답 칸 하나가 쓸 상자. 줄을 넘는 칸(pieces)은 첫 줄 상자에 쓴다.
const answerBoxOf = (answer) => {
  const firstPiece = answer.pieces?.[0];
  return firstPiece ? firstPiece.box || firstPiece : answer.box;
};

// 문항 영역과 답 칸을 모두 담는 상자. 지인에듀의 item 은 답 쓰는 여백을 빼고 준다.
const cropBoxOf = (field) => {
  const boxList = [field.item, ...(field.answers || []).flatMap((answer) => [answer.box, ...(answer.pieces || []).map((piece) => piece.box || piece)])]
    .filter((box) => box && Number.isFinite(box.x));
  const left = Math.min(...boxList.map((box) => box.x));
  const top = Math.min(...boxList.map((box) => box.y));
  const right = Math.max(...boxList.map((box) => box.x + box.w));
  const bottom = Math.max(...boxList.map((box) => box.y + box.h));
  return { x: left, y: top, w: right - left, h: bottom - top };
};

// 좌표 key 의 문항 번호는 세트 안의 차례(1부터)다. 서술형은 school.number 가 "서술형 1" 처럼 와서 차례로 찾는다.
const fieldNumberOf = (index) => String(index + 1);

// 번호 칸에 쓸 이름. 서술형은 "서1" 처럼 줄인다.
const numberLabelOf = (page, index) => {
  const schoolNumber = String(page.school?.number ?? index + 1);
  if (/^\d+$/.test(schoolNumber)) return schoolNumber;
  const digit = schoolNumber.match(/\d+/)?.[0] ?? "";
  return schoolNumber.startsWith("서술") ? `서${digit}` : schoolNumber.slice(0, 3);
};

// 손으로 그린 듯한 동그라미. 같은 자리에는 늘 같은 모양이 나오도록 seed 로 흔든다.
const HandCircle = ({ left, top, width, height, color, strokeWidth, seed }) => {
  const random = (step) => {
    const value = Math.sin(seed * 12.9898 + step * 78.233) * 43758.5453;
    return value - Math.floor(value);
  };
  const pointList = [];
  const turn = 2.15;
  const stepCount = 36;
  for (let step = 0; step <= stepCount; step += 1) {
    const angle = -Math.PI / 2.4 + (step / stepCount) * Math.PI * turn;
    const wobble = 1 + (random(step) - 0.5) * 0.08;
    pointList.push([
      50 + Math.cos(angle) * 46 * wobble,
      50 + Math.sin(angle) * 44 * wobble,
    ]);
  }
  const path = pointList.map(([x, y], index) => `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  return (
    <svg
      className="exam-paper__mark"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ left, top, width, height }}
    >
      <path d={path} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const HandSlash = ({ left, top, size }) => (
  <svg className="exam-paper__mark" viewBox="0 0 100 100" style={{ left, top, width: size, height: size }}>
    <path d="M18 88 L84 10" fill="none" stroke="#E03131" strokeWidth="3.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
  </svg>
);

const useElementWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

const useImageSize = (source) => {
  const [size, setSize] = useState(null);
  useEffect(() => {
    if (!source) return;
    setSize(null);
    const image = new Image();
    image.onload = () => setSize({ width: image.naturalWidth, height: image.naturalHeight });
    image.src = source;
  }, [source]);
  return size;
};

// 쪽 그림의 한 영역(box, 비율)을 폭에 맞게 잘라 보여 주고, 그 위에 표시를 얹는다.
// 영역 안의 비율 좌표를 화면 픽셀로 바꾸는 함수(toPixel)를 children 에 넘긴다.
const PaperCrop = ({ image, box, maxScale, children }) => {
  const [containerRef, containerWidth] = useElementWidth();
  const imageSize = useImageSize(image);
  const crop = box || { x: 0, y: 0, w: 1, h: 1 };

  const ready = imageSize && containerWidth > 0;
  const scale = ready ? Math.min(containerWidth / (crop.w * imageSize.width), maxScale ?? Infinity) : 0;
  const toPixel = (ratioBox) => ({
    left: (ratioBox.x - crop.x) * imageSize.width * scale,
    top: (ratioBox.y - crop.y) * imageSize.height * scale,
    width: (ratioBox.w ?? 0) * imageSize.width * scale,
    height: (ratioBox.h ?? 0) * imageSize.height * scale,
  });

  return (
    <div ref={containerRef} className="exam-paper__crop" style={{ height: ready ? crop.h * imageSize.height * scale : 240 }}>
      {ready && (
        <div className="exam-paper__crop-inner" style={{ width: crop.w * imageSize.width * scale }}>
          <img
            src={image}
            alt=""
            draggable={false}
            style={{
              width: imageSize.width * scale,
              left: -crop.x * imageSize.width * scale,
              top: -crop.y * imageSize.height * scale,
            }}
          />
          {children(toPixel, scale)}
        </div>
      )}
    </div>
  );
};

const expandedBox = (box) => {
  const x = Math.max(0, box.x - CROP_MARGIN);
  const y = Math.max(0, box.y - CROP_MARGIN);
  return {
    x,
    y,
    w: Math.min(1 - x, box.w + CROP_MARGIN * 2),
    h: Math.min(1 - y, box.h + CROP_MARGIN * 2),
  };
};

// 표시: 고른 선지에 연필 동그라미, 채점 뒤 번호에 빨간 O 또는 빗금, 틀렸으면 정답 선지에 빨간 동그라미
const GradingMarks = ({ field, page, toPixel, seed }) => {
  const chosenList = Array.isArray(page.input) ? page.input : page.input ? [page.input] : [];
  const answerList = Array.isArray(page.answer) ? page.answer : [page.answer];
  const optionBoxOf = (value) => (field.options || []).find((option) => Number(option.value) === Number(value))?.box;
  const numberPixel = field.number ? toPixel({ ...field.number, w: 0, h: 0 }) : null;
  const numberSize = Math.max(26, (field.options?.[0] ? toPixel(field.options[0].box).height : 20) * 1.9);
  return (
    <>
      {chosenList.map((value) => {
        const optionBox = optionBoxOf(value);
        if (!optionBox) return null;
        const pixel = toPixel(optionBox);
        const size = Math.max(pixel.width, pixel.height) * 1.6;
        return (
          <HandCircle
            key={`chosen-${value}`}
            left={pixel.left + pixel.width / 2 - size / 2}
            top={pixel.top + pixel.height / 2 - size / 2}
            width={size}
            height={size}
            color="#3b3b3b"
            strokeWidth={2}
            seed={seed + Number(value)}
          />
        );
      })}
      {page.isSubmitted && !page.isCorrect && field.type === "choice" &&
        answerList.filter((value) => !chosenList.map(Number).includes(Number(value))).map((value) => {
          const optionBox = optionBoxOf(value);
          if (!optionBox) return null;
          const pixel = toPixel(optionBox);
          const size = Math.max(pixel.width, pixel.height) * 2.2;
          return (
            <HandCircle
              key={`answer-${value}`}
              left={pixel.left + pixel.width / 2 - size / 2}
              top={pixel.top + pixel.height / 2 - size / 2}
              width={size}
              height={size}
              color="#E03131"
              strokeWidth={2.4}
              seed={seed + 40 + Number(value)}
            />
          );
        })}
      {page.isSubmitted && numberPixel && (page.isCorrect ? (
        <HandCircle
          left={numberPixel.left - numberSize / 2}
          top={numberPixel.top - numberSize / 2}
          width={numberSize}
          height={numberSize}
          color="#E03131"
          strokeWidth={2.6}
          seed={seed + 90}
        />
      ) : (
        <HandSlash left={numberPixel.left - numberSize / 2} top={numberPixel.top - numberSize / 2} size={numberSize} />
      ))}
    </>
  );
};

const ExamPaperPage = ({
  isReview,
  section,
  pageList,
  setPageList,
  currentIndex,
  setCurrentIndex,
  handlePreviousPage,
  handleNextPage,
}) => {
  const dispatch = useDispatch();
  const examPageList = section.question.exam_pages;
  const fragmentsByNumber = useMemo(() => fieldFragmentsOf(examPageList), [examPageList]);
  const [isWholePage, setIsWholePage] = useState(false);
  const [draftList, setDraftList] = useState([]);
  const [chosenDraft, setChosenDraft] = useState([]);

  const currentPage = pageList[currentIndex] || {};
  const number = fieldNumberOf(currentIndex);
  const fragmentList = fragmentsByNumber[number] || [];
  const field = fragmentList[0]?.field;
  const examPage = fragmentList[0] ? examPageList[fragmentList[0].pageIndex] : examPageList[0];
  const isLocked = currentPage.isSubmitted || isReview;
  const blankCount = currentPage.blankLabelList?.length || 1;
  // 답 칸 차례(index)대로 [{answer, fragmentIndex}]
  const answerSlotList = fragmentList
    .flatMap((fragment, fragmentIndex) => (fragment.field.answers || []).map((answer) => ({ answer, fragmentIndex })))
    .sort((one, other) => (one.answer.index ?? 0) - (other.answer.index ?? 0));

  useEffect(() => {
    setChosenDraft([]);
    const savedInput = String(currentPage.input ?? "");
    setDraftList(blankCount > 1 ? savedInput.split(", ").slice(0, blankCount) : [savedInput]);
    // 문항이 바뀔 때만 입력 칸을 다시 채운다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const savePage = (changes) => {
    const newPageList = pageList.map((page, index) => (index === currentIndex ? { ...page, ...changes } : page));
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const chooseOption = (value) => {
    if (isLocked) return;
    const numberValue = Number(value);
    if (currentPage.isMultipleAnswer) {
      setChosenDraft((previous) =>
        previous.includes(numberValue) ? previous.filter((one) => one !== numberValue) : [...previous, numberValue]
      );
      return;
    }
    savePage({ input: numberValue, isCorrect: numberValue === Number(currentPage.answer), isSubmitted: true });
  };

  const submitMultipleChoice = () => {
    const answerList = (currentPage.answer || []).map(Number).sort();
    const chosenList = [...chosenDraft].sort();
    savePage({
      input: chosenList,
      isCorrect: answerList.length === chosenList.length && answerList.every((value, index) => value === chosenList[index]),
      isSubmitted: true,
    });
  };

  const submitText = () => {
    const answerList = String(currentPage.answer ?? "").split(", ");
    const isCorrect =
      blankCount > 1
        ? answerList.every((answer, index) => normalizeEnglishAnswer(draftList[index] || "") === normalizeEnglishAnswer(answer))
        : normalizeEnglishAnswer(draftList[0] || "") === normalizeEnglishAnswer(String(currentPage.answer ?? ""));
    savePage({ input: draftList.join(", "), isCorrect, isSubmitted: true });
  };

  const isChoice = Array.isArray(currentPage.choices) && currentPage.choices.length > 0;
  const isWritingOnPaper = !isChoice && answerSlotList.length > 0 && answerSlotList.length === blankCount;
  const changeDraft = (blankIndex, value) =>
    setDraftList((previous) => {
      const next = [...previous];
      next[blankIndex] = value;
      return next;
    });
  const shownPage = currentPage.isMultipleAnswer && !currentPage.isSubmitted ? { ...currentPage, input: chosenDraft } : currentPage;
  const answeredCount = pageList.filter((page) => page.isSubmitted).length;

  return (
    <div className="exam-paper">
      <div className="exam-paper__numbers">
        {pageList.map((page, index) => (
          <button
            key={index}
            type="button"
            className={[
              "exam-paper__number",
              index === currentIndex && "is-current",
              page.isSubmitted && (page.isCorrect ? "is-correct" : "is-wrong"),
            ].filter(Boolean).join(" ")}
            onClick={() => setCurrentIndex(index)}
          >
            {numberLabelOf(page, index)}
          </button>
        ))}
      </div>

      <div className="exam-paper__body">
        <div className="exam-paper__sheet">
          <div className="exam-paper__sheet-head">
            <span>{examPage ? `${examPage.page}쪽` : ""}</span>
            <span>{answeredCount} / {pageList.length}</span>
            <button type="button" onClick={() => setIsWholePage((previous) => !previous)}>
              {isWholePage ? "문항 크게" : "쪽 전체"}
            </button>
          </div>

          {examPage && (isWholePage || !field) ? (
            <PaperCrop image={examPage.image}>
              {(toPixel) =>
                (examPage.fields || []).map((pageField) => {
                  const pageNumber = String(pageField.key || "").split(":")[1];
                  const pageIndex = Number(pageNumber) - 1;
                  if (!pageList[pageIndex]) return null;
                  const pixel = toPixel(pageField.item);
                  return (
                    <div key={pageField.key}>
                      <button
                        type="button"
                        className={`exam-paper__item${pageIndex === currentIndex ? " is-current" : ""}`}
                        style={pixel}
                        onClick={() => {
                          setCurrentIndex(pageIndex);
                          setIsWholePage(false);
                        }}
                      />
                      <GradingMarks field={pageField} page={pageList[pageIndex]} toPixel={toPixel} seed={pageIndex * 7} />
                      {pageList[pageIndex].isSubmitted &&
                        (pageField.answers || []).map((answer) => (
                          <div key={answer.index} className="exam-paper__written" style={toPixel(answerBoxOf(answer))}>
                            {String(pageList[pageIndex].input ?? "").split(", ")[answer.index] ?? ""}
                          </div>
                        ))}
                    </div>
                  );
                })
              }
            </PaperCrop>
          ) : (
            fragmentList.map((fragment, fragmentIndex) => (
              <PaperCrop
                key={fragmentIndex}
                image={examPageList[fragment.pageIndex].image}
                box={expandedBox(cropBoxOf(fragment.field))}
                maxScale={MAX_CROP_SCALE}
              >
                {(toPixel) => (
                  <>
                    {isChoice && !isLocked &&
                      (fragment.field.options || []).map((option) => {
                        const pixel = toPixel(option.box);
                        const size = Math.max(MIN_TOUCH_SIZE, pixel.height * 1.6);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className="exam-paper__option"
                            aria-label={`${option.value}번`}
                            style={{
                              left: pixel.left + pixel.width / 2 - size / 2,
                              top: pixel.top + pixel.height / 2 - size / 2,
                              width: size,
                              height: size,
                            }}
                            onClick={() => chooseOption(option.value)}
                          />
                        );
                      })}
                    <GradingMarks field={fragment.field} page={shownPage} toPixel={toPixel} seed={currentIndex * 7 + fragmentIndex} />
                    {isWritingOnPaper &&
                      answerSlotList.map((slot, blankIndex) => {
                        if (slot.fragmentIndex !== fragmentIndex) return null;
                        const pixel = toPixel(answerBoxOf(slot.answer));
                        return (
                          <textarea
                            key={blankIndex}
                            className={`exam-paper__write${currentPage.isSubmitted ? (currentPage.isCorrect ? " is-correct" : " is-wrong") : ""}`}
                            style={{ ...pixel, fontSize: Math.min(22, Math.max(16, pixel.height * 0.36)) }}
                            aria-label={slot.answer.label || `${blankIndex + 1}번 칸`}
                            value={draftList[blankIndex] ?? ""}
                            disabled={isLocked}
                            spellCheck={false}
                            autoCapitalize="off"
                            onChange={(event) => changeDraft(blankIndex, event.target.value)}
                          />
                        );
                      })}
                  </>
                )}
              </PaperCrop>
            ))
          )}
        </div>

        {!isWholePage && field && (
          <div className="exam-paper__answer">
            {isChoice && currentPage.isMultipleAnswer && !isLocked && (
              <button type="button" className="exam-paper__submit" disabled={chosenDraft.length === 0} onClick={submitMultipleChoice}>
                제출
              </button>
            )}
            {!isChoice && (
              <>
                {!isWritingOnPaper && Array.from({ length: blankCount }).map((_, blankIndex) => (
                  <label key={blankIndex} className="exam-paper__blank">
                    {blankCount > 1 && <b>{currentPage.blankLabelList[blankIndex]}</b>}
                    <textarea
                      rows={blankCount > 1 ? 1 : 3}
                      value={draftList[blankIndex] ?? ""}
                      disabled={isLocked}
                      onChange={(event) => changeDraft(blankIndex, event.target.value)}
                    />
                  </label>
                ))}
                {!isLocked && (
                  <button type="button" className="exam-paper__submit" disabled={!draftList.some((one) => one?.trim())} onClick={submitText}>
                    제출
                  </button>
                )}
              </>
            )}
            {currentPage.isSubmitted && !currentPage.isCorrect && !isChoice && (
              <p className="exam-paper__right-answer">{String(currentPage.answer ?? "")}</p>
            )}
          </div>
        )}
      </div>

      <div className="exam-paper__nav">
        <button type="button" onClick={handlePreviousPage} disabled={currentIndex === 0}>
          이전
        </button>
        <button type="button" className="is-primary" onClick={handleNextPage}>
          {currentIndex === pageList.length - 1 ? "다 풀었어요" : "다음"}
        </button>
      </div>
    </div>
  );
};

export default ExamPaperPage;
